// Idle-session timeout (client-enforced, server-backed).
//
// After IDLE_LIMIT_MS without user interaction a warning modal opens; if it
// isn't acknowledged within WARNING_MS we call logout(), which deletes the
// refresh token server-side — the session is dead, not just hidden. The
// 15-minute access token and the refresh-token expiry remain the server-side
// backstops for browsers where JS never got the chance to run.

import { useEffect, useRef, useState } from "react";

export const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000; // 8 hours
export const WARNING_MS = 2 * 60 * 1000; // 2 minutes to answer the prompt

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll"];
// Don't reset the timer more than once per (this many) ms — scroll fires a lot.
const THROTTLE_MS = 30_000;

export function useIdleLogout(onTimeout: () => void): {
  warning: boolean;
  staySignedIn: () => void;
} {
  const [warning, setWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  const lastReset = useRef(Date.now());
  const warningSince = useRef<number | null>(null);
  const timeoutRef = useRef(onTimeout);
  timeoutRef.current = onTimeout;

  useEffect(() => {
    function touch(): void {
      const now = Date.now();
      if (now - lastReset.current < THROTTLE_MS) return;
      lastReset.current = now;
      // Interaction while the warning is up does NOT dismiss it — that's
      // what the explicit button is for (a stray mouse move shouldn't
      // silently extend an unattended session).
      if (warningSince.current === null) lastActivity.current = now;
    }

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    // visibilitychange is typed on Document, not Window.
    document.addEventListener("visibilitychange", touch, { passive: true });
    const interval = window.setInterval(() => {
      const now = Date.now();
      if (warningSince.current !== null) {
        if (now - warningSince.current >= WARNING_MS) {
          warningSince.current = null;
          setWarning(false);
          timeoutRef.current();
        }
        return;
      }
      if (now - lastActivity.current >= IDLE_LIMIT_MS) {
        warningSince.current = now;
        setWarning(true);
      }
    }, 15_000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch));
      document.removeEventListener("visibilitychange", touch);
      window.clearInterval(interval);
    };
  }, []);

  function staySignedIn(): void {
    warningSince.current = null;
    lastActivity.current = Date.now();
    lastReset.current = Date.now();
    setWarning(false);
  }

  return { warning, staySignedIn };
}
